import React, { useEffect, useState } from 'react';
import { RootState, useDispatch, useSelector } from '../../redux/store';
import getCertIssueDateQuery from '../../utils/mill-sheet-search/getCertIssueDateQuery';
import getSizeQuery from '../../utils/mill-sheet-search/getSizeQuery';
import { SEARCH_FIELD_NAMES, SEARCH_QUERY_NAMES } from '../../constants';
import type { SearchFieldName, SearchOptions, SearchOptionQueries } from '../../types/millSheetSearch';
import { QueryOptions } from '@testing-library/react';

/**
 * 複数のクエリ文字列は' AND 'で区切られている。
 * 個別のクエリ文字列が「メーカー名:日本製鉄」のように「検索オプション項目名」＋「:」＋「任意の文字列」である場合には
 * 該当する検索オプション項目のカラムが検索され、
 * そうでない場合にはカラムを特定せずに全体のカラムから検索される（フリーワード検索）。
 * 例えば「メーカー名:日本製鉄 AND 君津」というクエリの場合は、
 * メーカー名のカラムが「日本製鐵」でかつ、いずれかのカラムに「君津」が含まれるデータがヒットする。
 *
 * 文字列クエリを変更した場合は、（検索オプションがどうであれ）文字列クエリから検索クエリを作成する。
 * 検索オプションを変更した場合も同様に、検索オプションからクエリを作成するが、
 * 文字列クエリ内のフリーワード検索部分を文字列クエリに保持しておく必要がある。
 * @param options: 検索オプションとして入力された値のObject
 * @param query フリーワードとして入力された文字列
 * @returns クエリ文字列
 */
const useConstructedQuery = (options: SearchOptions, query: string): string => {
  const [constructedQuery, setConstructedQuery] = useState<string>('');

  // サンプルデータ
  // const options: SearchOptions = {
  //   feInputMakerNameJa: '',
  //   feInputMakerNameEn: '',
  //   feInputMaterialTypeJa: '',
  //   feInputMaterialTypeEn: '',
  //   feInputTradeOrg: '',
  //   feInputCertIssueStartDate: '',
  //   feInputCertIssueEndDate: '2023-01-09',
  //   feInputContractNumber: '',
  //   feInputStandard: '',
  //   feInputMaterialSizeDStart: '16',
  //   feInputMaterialSizeDEnd: '17',
  //   feInputMaterialSizeTStart: '',
  //   feInputMaterialSizeTEnd: '',
  //   feInputMaterialSizeLStart: '',
  //   feInputMaterialSizeLEnd: '',
  //   feInputMeltNumber: '',
  //   feInputCustomer1: '',
  //   feInputCustomer2: 'A2041',
  //   feInputCustomer3: '',
  //   feInputPlaceCode: '',
  //   feInputCertNumber: '',
  // };

  // サンプルデータ
  // const query: string = 'メーカー名:日本製鉄 AND 証明書発行年月日:[2020-06-02 TO *] AND 君津 AND SAN-YU';
  // const query: string = 'メーカー名:日本製鉄 AND 証明書発行年月日:[2020-06-02 TO *]';
  // const query: string = '君津 AND SAN-YU';

  // useEffect(() => {
  //   console.log(options, constructedQuery);
  // }, [options, constructedQuery]);

  // useEffect(() => {
  //   console.log(query);
  // }, [query]);

  useEffect(() => {
    // 検索オプション項目のクエリを構築
    // 例）['メーカー名:日本製鉄', '溶解番号:ABC']というような文字列の配列を作る
    // ========================================

    // 範囲選択（Start - End）がある検索オプションをそれぞれ1つにまとめる
    const certIssueDateQuery: string = getCertIssueDateQuery(options[SEARCH_FIELD_NAMES.START_DATE], options[SEARCH_FIELD_NAMES.END_DATE]);
    const sizeDQuery: string = getSizeQuery(options[SEARCH_FIELD_NAMES.MATERIAL_SIZE_D_START], options[SEARCH_FIELD_NAMES.MATERIAL_SIZE_D_END]);
    const sizeTQuery: string = getSizeQuery(options[SEARCH_FIELD_NAMES.MATERIAL_SIZE_T_START], options[SEARCH_FIELD_NAMES.MATERIAL_SIZE_T_END]);
    const sizeLQuery: string = getSizeQuery(options[SEARCH_FIELD_NAMES.MATERIAL_SIZE_L_START], options[SEARCH_FIELD_NAMES.MATERIAL_SIZE_L_END]);
    // console.log(certIssueDateQuery);
    const queryByOptions: SearchOptionQueries = Object.assign({}, options, {
      feInputCertIssueDate: certIssueDateQuery,
      feInputMaterialSizeD: sizeDQuery,
      feInputMaterialSizeT: sizeTQuery,
      feInputMaterialSizeL: sizeLQuery,
    });
    // console.log(queryByOptions);

    // オプション検索項目から検索文字列を生成する
    const optionKeys: string[] = Object.keys(queryByOptions);
    // console.log(optionKeys);
    const optionList: string[] = optionKeys.reduce((acc, cur) => {
      // console.log(cur, queryByOptions[cur], SEARCH_QUERY_NAMES[cur]);
      // フィールドが空文字列でなく、かつfeInputCertIssueStartDateでもfeInputCertIssueEndDateでもない場合はクエリとして構築する
      if (queryByOptions[cur] !== '' && SEARCH_QUERY_NAMES[cur] !== undefined) {
        const query: string = SEARCH_QUERY_NAMES[cur] + ':' + queryByOptions[cur];
        return [...acc, query];
      } else {
        return acc;
      }
    }, []);
    // console.log(optionList);
    const optionString: string = optionList.join(' AND ');

    // メイン検索窓の文字列から検索オプション項目を除いて
    // フリーワード検索用のクエリを構築
    // ========================================

    // サンプルデータ
    // const query: string = 'メーカー名:日本製鉄 AND 証明書発行年月日:[2020-06-02 TO *] AND 君津 AND SAN-YU';
    // const query: string = 'メーカー名:日本製鉄 AND 証明書発行年月日:[2020-06-02 TO *]';
    // const query: string = '君津 AND SAN-YU';

    // フリーワード検索の文字列リスト
    const queryList: string[] = query.split(' AND ').reduce((acc, cur) => {
      // コロンがあり、かつコロンの前が検索オプションの項目名であるならば
      // 検索オプションによるクエリなので検索用フリーワードから除外する
      const assumedOptionName: string = cur.split(':')[0];
      const isOptionyName: boolean = Object.values(SEARCH_QUERY_NAMES).find((name) => name === assumedOptionName) !== undefined;
      // console.log(assumedOptionName, isOptionyName);
      if (cur.indexOf(':') > -1 && isOptionyName) {
        return acc;
      } else {
        return [...acc, cur];
      }
    }, []);
    // console.log(queryList);
    // フリーワード検索文字列
    const queryString: string = queryList.join(' AND ');

    // クエリの性質上、オプション検索 + フリーワード検索の順番に文字列を結合する
    // オプション検索あり、フリーワード検索なしの場合： ' AND '不要
    // オプション検索なし、フリーワード検索ありの場合： ' AND '不要
    // オプション検索あり、フリーワード検索ありの場合： ' AND '必要
    const spaceAndSpace = optionString.length && queryString.length ? ' AND ' : '';
    const mergedQuery = optionString + spaceAndSpace + queryString;
    // console.log(mergedQuery);
    setConstructedQuery(mergedQuery);
  }, [options, query]);

  return constructedQuery;
};

export default useConstructedQuery;
